/** Presentation only. The existing hardware adapter owns commands and report validation. */
export type MeasurementKind = 'combined' | 'temperature';
export type MeasurementPhase = 'waiting' | 'measuring' | 'delayed' | 'complete' | 'error' | 'cancelled';
export const MEASUREMENT_ANIMATION_MS = 60_000;

export interface MeasurementTiming {
  /** Bracelet acknowledgement time, never the button press/queued time. */
  startedAt: number | null;
  /** True only for a fresh, validated report belonging to this active session. */
  resultReady: boolean;
  /** Existing command acquisition deadline; do not change the hardware timeout. */
  timeoutAt: number;
  failed?: boolean;
  cancelled?: boolean;
}

export function measurementFrame(input: MeasurementTiming, now: number) {
  const started = input.startedAt !== null && Number.isFinite(input.startedAt);
  const elapsedMs = started ? Math.max(0, now - input.startedAt!) : 0;
  let phase: MeasurementPhase;
  if (input.cancelled) phase = 'cancelled';
  else if (input.failed) phase = 'error';
  else if (input.resultReady) phase = 'complete';
  else if (!input.resultReady && now >= input.timeoutAt) phase = 'error';
  else if (!started) phase = 'waiting';
  else phase = elapsedMs >= MEASUREMENT_ANIMATION_MS ? 'delayed' : 'measuring';
  const progress = phase === 'complete' ? 1 : started ? Math.min(.98, elapsedMs / MEASUREMENT_ANIMATION_MS) : 0;
  return { phase, elapsedMs, progress };
}

export function measurementClock(elapsedMs: number) {
  const seconds = Math.max(0, Math.floor(Number.isFinite(elapsedMs) ? elapsedMs / 1000 : 0));
  return `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
}

export interface MeasurementValues {
  pulse?: number | null;
  systolic?: number | null;
  diastolic?: number | null;
  oxygen?: number | null;
  temperature?: number | null;
}

export function reading(value: number | null | undefined, locale: string, digits = 0) {
  return typeof value === 'number' && Number.isFinite(value)
    ? new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value)
    : "—";
}

export function pressure(values: MeasurementValues, locale: string) {
  const systolic = reading(values.systolic, locale), diastolic = reading(values.diastolic, locale);
  return systolic === "—" && diastolic === "—" ? "—" : `${systolic}/${diastolic}`;
}
